'use client'
import { useState, useEffect } from "react"
import { z } from "zod"
import { useFieldArray, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Plus, HelpCircle, GripVertical, Trash2, X, Edit2, Loader2, Info } from "lucide-react"
import { toast } from "sonner"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { useAuth } from "@clerk/clerk-react"
import DeleteQuestion from "../forms/deleteQuestion"
import { ClipLoader } from "react-spinners";

const questionSchema = z.object({
    questionId: z.union([z.string(), z.number()]).optional(),
    type: z.enum(['multiple_choice', 'description', 'true_false', 'numeric']),
    question_text: z.string().min(1, "Question is required"),
    score: z.number().min(1, "Score is required"),
    order: z.number().min(1, "Order is required"),
    question_content: z.string().optional(),
    explanation: z.string().optional(),
    answers: z.array(z.object({
        id: z.number().optional(),
        text: z.string().min(1, "Answer is required"),
        is_correct: z.boolean(),
        question_id: z.number().optional(),
    })).optional().default([]),
    answer_text: z.string().optional(),
    isExisting: z.boolean().optional().default(false),
}).refine((data) => {
    if (data.type === 'multiple_choice') {
        const validAnswers = (data.answers || []).filter(a => a.text && a.text.trim() !== "")
        return validAnswers.length >= 2 && validAnswers.some(a => a.is_correct)
    }
    if (data.type === 'numeric') {
        const validAnswers = (data.answers || []).filter(answer => answer.text && answer.text.trim() !== "")
        return validAnswers.length >= 1 && validAnswers.some(a => a.is_correct)
    }
    return true
}, {
    message: "Multiple choice must have at least 2 answers and a correct answer selected",
    path: ["answers"]
})

const addQuestionsSchema = z.object({
    title: z.string().min(1, "Quiz title is required"),
    description: z.string().optional(),
    time_limit: z.coerce.number().min(0, "Time limit cannot be negative"),
    maximum_attempts: z.coerce.number().min(0, "Maximum attempts cannot be negative"),
    passmark: z.coerce.number().min(0).max(100, "Passmark must be between 0 and 100"),
    questions: z.array(questionSchema).min(1, "At least one question is required"),
})

export function AddQuestionToQuizDialog({ quiz, onQuestionsAdded, children, refetchTopics }) {
    const [open, setOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [addQuestionOpen, setAddQuestionOpen] = useState(false)
    const [editQuestionOpen, setEditQuestionOpen] = useState(false)
    const [editingQuestionIndex, setEditingQuestionIndex] = useState(null)
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
    const { getToken } = useAuth()

    const form = useForm({
        resolver: zodResolver(addQuestionsSchema),
        defaultValues: {
            title: quiz?.title || "",
            description: quiz?.description || "",
            time_limit: quiz?.time_limit || 0,
            maximum_attempts: quiz?.maximum_attempts || 0,
            passmark: quiz?.passmark || 0,
            questions: [],
        },
    })

    const { fields: questions, append, remove, update, replace } = useFieldArray({
        control: form.control,
        name: "questions",
    })



    const fetchExistingQuestions = async (quizId) => {
        try {
            setIsLoadingQuestions(true)
            const token = await getToken()
            if (!token) {
                toast.error("Authentication required")
                return []
            }

            const requestConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }

            const response = await axios.get(api_endpoint + `/quiz/questions/${quizId}`, requestConfig)
            console.log("Quiz questions response:", response.data)

            if (response.data?.status === "success" && Array.isArray(response.data.questions_and_answers)) {
                const transformedQuestions = response.data.questions_and_answers.map(item => {

                    const { question, answers } = item

                    let patchedAnswers = answers.map(a => ({
                        id: a.id,
                        text: a.answer_text,
                        is_correct: !!a.is_correct,
                        question_id: a.question_id
                    }));

                    // Patch for true/false questions
                    if (question.type === "true_false") {
                        // Ensure both "True" and "False" options exist
                        const texts = patchedAnswers.map(a => a.text?.toLowerCase());
                        if (!texts.includes("true")) {
                            patchedAnswers.push({ text: "True", is_correct: false });
                        }
                        if (!texts.includes("false")) {
                            patchedAnswers.push({ text: "False", is_correct: false });
                        }
                        // Only keep "True" and "False" options, and order them
                        patchedAnswers = [
                            patchedAnswers.find(a => a.text?.toLowerCase() === "true") || { text: "True", is_correct: false },
                            patchedAnswers.find(a => a.text?.toLowerCase() === "false") || { text: "False", is_correct: false }
                        ];
                    }

                    return {
                        id: Number(question.id),
                        questionId: Number(question.id),
                        type: question.type,
                        question_text: question.question_text,
                        score: question.score || 1,
                        order: question.order || 1,
                        question_content: question.question_content || "",
                        explanation: question.explanation || "",
                        answers: patchedAnswers,
                        answer_text: question.type === "description" ? answers[0]?.answer_text || "" : "",
                        isExisting: true,
                        isModified: false,
                        isDeleted: false,
                    }
                })
                return transformedQuestions
            } else {
                console.error("Failed to fetch questions:", response.data)
                return []
            }
        } catch (error) {
            console.error("Error fetching questions:", error)
            // Don't show error toast for empty questions - it's normal for new quizzes
            if (error.response?.status !== 404) {
                toast.error("Failed to load existing questions")
            }
            return []
        } finally {
            setIsLoadingQuestions(false)
        }
    }



    const [lastQuizId, setLastQuizId] = useState(null)

    useEffect(() => {
        if (open && quiz?.id && quiz.id !== lastQuizId) {
            const loadQuestions = async () => {
                const existingQuestions = await fetchExistingQuestions(quiz.id)
                replace(existingQuestions)
                setLastQuizId(quiz.id)
            }
            loadQuestions()
        }
        if (!open) setLastQuizId(null)
    }, [open, quiz?.id])

    useEffect(() => {
        if (quiz) {
            form.reset({
                title: quiz.title || "",
                description: quiz.description || "",
                time_limit: quiz.time_limit || 0,
                maximum_attempts: quiz.maximum_attempts || 0,
                passmark: quiz.passmark || 0,
            })
        }
    }, [quiz, form])

    const onQuestionAdded = (newQuestion) => {
        const questionWithOrder = {
            ...newQuestion,
            order: questions.length + 1,
            isExisting: false
        }
        console.log("Appending question:", questionWithOrder)
        append(questionWithOrder)
        setAddQuestionOpen(false)
        console.log("Questions after append:", form.getValues("questions"))
    }

    const onQuestionUpdated = (updatedQuestion) => {
        if (editingQuestionIndex !== null) {
            update(editingQuestionIndex, updatedQuestion)
            setEditQuestionOpen(false)
            setEditingQuestionIndex(null)
        }
    }

    const handleEditQuestion = (index) => {
        console.log("Opening edit dialog for question at index:", index)
        setEditingQuestionIndex(index)
        setEditQuestionOpen(true)
    }

    const handleCorrectChoiceChange = (questionIndex, answerIdOrIndex) => {
        const currentQuestions = form.getValues('questions')
        const question = currentQuestions[questionIndex]

        // answers are objects
        question.answers = question.answers.map((a, idx) => ({
            ...a,
            is_correct: idx === answerIdOrIndex
        }));


        form.setValue('questions', currentQuestions)
    }

    function areQuestionsValid(questions) {
        return questions.every(q => {
            if (q.type === "multiple_choice") {
                const answers = q.answers.map(a => a.text?.trim());
                const hasEnoughAnswers = answers.filter(a => a).length >= 2;
                const hasCorrect = q.answers.some(a => a.is_correct);
                return hasEnoughAnswers && hasCorrect;
            }
            if (q.type === "numeric") {
                const answers = q.answers.map(a => a.text?.trim());
                const hasEnoughAnswers = answers.filter(a => a).length >= 1; // allow 1 or more
                const hasCorrect = q.answers.some(a => a.is_correct);
                return hasEnoughAnswers && hasCorrect;
            }
            if (q.type === "true_false") {
                const answers = q.answers.map(a => a.text?.trim().toLowerCase());
                const hasTrue = answers.includes("true");
                const hasFalse = answers.includes("false");
                const hasCorrect = q.answers.some(a => a.is_correct);
                return answers.length === 2 && hasTrue && hasFalse && hasCorrect;
            }
            return true;
        });
    }

    const onSubmit = async (values) => {
        console.log("Submitting questions:", values.questions)
        console.log("Form submitted!", values);
        console.log("Edit dialog submitted by user:", values)
        try {
            setIsSubmitting(true)
            const token = await getToken()
            if (!token) {
                toast.error("Authentication required")
                return
            }

            if (!quiz?.id) {
                toast.error("No quiz selected.")
                return
            }

            const requestConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }

            let createdCount = 0
            let updatedCount = 0
            const updatedQuestions = [...values.questions]

            // Process questions
            for (const [i, question] of values.questions.entries()) {

                if (question.isExisting && question.questionId && typeof question.questionId === "number") {

                    const original = questions.find(q => q.questionId === question.questionId)

                    const isNoChange =
                        question.question_text === original?.question_text &&
                        question.question_content === original?.question_content &&
                        question.type === original?.type &&
                        question.order === original?.order &&
                        question.score === original?.score &&
                        question.explanation === original?.explanation

                    if (isNoChange) {
                        continue
                    }

                    // Update existing question
                    const updatePayload = {
                        question_text: question.question_text,
                        question_content: question.question_content || "",
                        type: question.type,
                        order: question.order,
                        score: question.score,
                        explanation: question.explanation || null,
                    }

                    console.log("Updating question with payload:", updatePayload)

                    const updateResponse = await axios.put(api_endpoint + `/quiz/question/update/${question.questionId}`, updatePayload, requestConfig)

                    console.log("Question update API Response:", updateResponse.data)

                    if (updateResponse.data?.status !== "success") {
                        console.error("Question update failed:", updateResponse.data)
                        throw new Error(`Question update failed: ${updateResponse.data?.message || "Unkown error"}`)
                    }
                    updatedCount++
                } else {

                    // Create new question
                    if (!question.isExisting) {
                        let answersArray = [];
                        if (question.type === "multiple_choice" || question.type === "numeric") {
                            answersArray = question.answers.map(a => ({
                                text: a.text,
                                is_correct: !!a.is_correct
                            }));
                        } else if (question.type === "true_false") {
                            answersArray = [
                                { text: "True", is_correct: question.answers?.find(a => a.text === "True")?.is_correct || false },
                                { text: "False", is_correct: question.answers?.find(a => a.text === "False")?.is_correct || false }
                            ]
                        } else if (question.type === "description" && question.answer_text) {
                            answersArray = [{ text: question.answer_text, is_correct: true }];
                        }

                        const questionPayload = {
                            quiz_id: quiz.id,
                            question_text: question.question_text,
                            question_content: question.question_content || "",
                            type: question.type,
                            order: question.order,
                            score: question.score,
                            explanation: question.explanation || null,
                            answers: answersArray // Include answers directly in question creation
                        }

                        console.log("Creating question with payload:", questionPayload) // Debug log

                        // Create the question with answers included
                        const questionResponse = await axios.post(
                            api_endpoint + "/quiz/question/create",
                            questionPayload,
                            requestConfig
                        )

                        console.log("Question API Response:", questionResponse.data)

                        // Check if question was created successfully
                        if (questionResponse.data?.status !== "success") {
                            console.error("Question creation failed:", questionResponse.data)
                            throw new Error(`Question creation failed: ${questionResponse.data?.message || "Unknown error"}`)
                        }

                        // Update the question with the new ID from the response
                        const createdQuestionId = questionResponse.data?.question?.id
                        console.log("Question created successfully with ID:", createdQuestionId)
                        if (createdQuestionId) {
                            updatedQuestions[i] = {
                                ...updatedQuestions[i],
                                questionId: Number(createdQuestionId),
                                isExisting: true
                            }

                            // Update the form data with the new question ID
                            form.setValue(`questions.${i}.questionId`, Number(createdQuestionId))
                            form.setValue(`questions.${i}.isExisting`, true)

                            // Also update the questions array used by useFieldArray
                            update(i, updatedQuestions[i])
                        }

                        console.log("Question created successfully with ID:", createdQuestionId)
                        createdCount++
                    }
                }
            }

            const message = [
                createdCount > 0 && `Created ${createdCount} question ${createdCount !== 1 ? 's' : ''}`,
                updatedCount > 0 && `Updated ${updatedCount} question ${updatedCount !== 1 ? 's' : ''}`
            ].filter(Boolean).join('and')

            const refreshedQuestions = await fetchExistingQuestions(quiz.id)
            replace(refreshedQuestions)
            form.reset({
                ...form.getValues(),
                questions: refreshedQuestions
            })

            toast.success(`${message} for "${quiz.title}" successfully!`)
            setOpen(false)
            form.reset()
            if (onQuestionsAdded) onQuestionsAdded()
            if (refetchTopics) refetchTopics()

        } catch (error) {
            console.error("Failed to add questions:", error)
            toast.error(error.message || "Failed to add questions. Please try again.")
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCancel = () => {
        form.reset()
        replace([])
        setOpen(false)
        setLastQuizId(null)
    }

    // Calculate total points
    const totalPoints = questions.reduce((sum, q) => sum + (q.score || 0), 0)

    // Check if there are any questions
    const newQuestionsCount = questions.filter(q => !q.isExisting).length

    return (
        <>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    {children}
                </DialogTrigger>
                <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0" aria-describedby="add-questions-description">
                    <DialogHeader className="px-6 py-4 border-b shrink-0">
                        <DialogTitle className="font-heading">Add Questions to Quiz</DialogTitle>
                        <DialogDescription id="add-questions-description" className="font-body">
                            Adding questions to "{quiz?.title}". You can add multiple questions at once.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto px-6 py-0">
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="add-questions-form">
                                {/* Quiz details section */}
                                <div className="space-y-4 mt-1">
                                    <FormField
                                        control={form.control}
                                        name="title"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="font-heading">Quiz Title *</FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        placeholder="Intro to Machine Learning"
                                                        readOnly
                                                        className="bg-gray-100 font-body"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="description"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="font-heading">Description</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        {...field}
                                                        rows={3}
                                                        className="min-h-[120px] resize-none bg-gray-100 font-body"
                                                        placeholder="Describe what this quiz covers..."
                                                        readOnly
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="time_limit"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="font-heading">Time Limit (Minutes)</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} type="number" min="1" placeholder="30" readOnly className="bg-gray-100 font-body" />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="maximum_attempts"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel className="font-heading">Maximum Attempts</FormLabel>
                                                    <FormControl>
                                                        <Input {...field} type="number" min="1" placeholder="3" readOnly className="bg-gray-100 font-body" />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>

                                    <FormField
                                        control={form.control}
                                        name="passmark"
                                        render={({ field }) => {
                                            const value = field.value || 0
                                            const fillPercent = Math.min(Math.max(value, 0), 100)

                                            return (
                                                <FormItem>
                                                    <FormLabel className="font-heading">Passing Score (%)</FormLabel>
                                                    <FormControl>
                                                        <div className="flex items-center gap-3">
                                                            <input
                                                                type="number"
                                                                min={0}
                                                                max={100}
                                                                value={value}
                                                                onChange={(e) => field.onChange(Number(e.target.value))}
                                                                className="w-16 border rounded px-2 py-1 text-sm text-center font-body"
                                                                readOnly
                                                            />

                                                            <div className="flex-1">
                                                                <input
                                                                    type="range"
                                                                    min={0}
                                                                    max={100}
                                                                    value={value}
                                                                    disabled
                                                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-not-allowed"
                                                                    style={{
                                                                        background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${fillPercent}%, #e5e7eb ${fillPercent}%, #e5e7eb 100%)`
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )
                                        }}
                                    />
                                </div>

                                {/* Questions Section */}
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-semibold font-heading">Add Questions</h2>
                                    </div>
                                    <div className="text-sm text-gray-500 mb-4 font-body">
                                        {questions.length} questions • {totalPoints} points
                                    </div>

                                    <div className="space-y-4 py-2">
                                        {questions.map((question, index) => (
                                            <QuestionCard
                                                key={question.id || index}
                                                question={question}
                                                index={index}
                                                onRemove={() => remove(index)}
                                                onEdit={() => handleEditQuestion(index)}
                                                onCorrectChoiceChange={(answerId) => handleCorrectChoiceChange(index, answerId)}
                                            />
                                        ))}

                                        {questions.length === 0 && (
                                            <div className="text-center py-8 text-gray-500 rounded-lg mb-2 font-body">
                                                No questions added yet. Click "Add Question" to get started.
                                            </div>
                                        )}
                                    </div>

                                </div>
                                <Button
                                    variant="outline"
                                    type="button"
                                    onClick={() => setAddQuestionOpen(true)}
                                    className="border-red text-red hover:bg-blue hover:border-transparent hover:text-white rounded-[8px] w-full mt-2"
                                >
                                    Add Question <Plus className="w-4 h-4 mr-2" />
                                </Button>
                            </form>
                        </Form>
                    </div>

                    <div className="flex justify-between px-6 py-4 border-t shrink-0 gap-2">
                        <DialogClose asChild>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleCancel}
                                disabled={isSubmitting}
                                className="w-[100px] border-red text-red hover:bg-blue hover:text-white hover:border-transparent rounded-[8px] font-heading"
                            >
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            type="submit"
                            form="add-questions-form"
                            className="bg-red-500 text-white hover:bg-blue w-[100px] rounded-[8px] font-heading"
                            disabled={isSubmitting || questions.length === 0 || !areQuestionsValid(questions) || newQuestionsCount === 0}
                        >
                            {isSubmitting ? (
                                <span className="flex items-center justify-center w-full">
                                    <ClipLoader size={20} color="#fff" />
                                </span>
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <AddQuestionDialog
                open={addQuestionOpen}
                setOpen={setAddQuestionOpen}
                onQuestionAdded={onQuestionAdded}
                nextOrder={questions.length + 1}
            />

            <EditQuestionDialog
                open={editQuestionOpen}
                setOpen={setEditQuestionOpen}
                onQuestionUpdated={onQuestionUpdated}
                question={editingQuestionIndex !== null ? questions[editingQuestionIndex] : null}
            />
        </>
    )
}

// Question Card Component
function QuestionCard({ question, index, onRemove, onEdit, onCorrectChoiceChange }) {
    console.log("Rendering QuestionCard, question.questionId:", question.questionId, "Type:", typeof question.questionId);

    // ADD THIS DEBUGGING
    console.log("QuestionCard Debug:", {
        type: question.type,
        hasAnswers: !!question.answers,
        answersLength: question.answers?.length,
        answersData: question.answers,
        shouldRenderAnswers: ['multiple_choice', 'numeric', 'true_false'].includes(question.type) && question.answers
    })

    return (
        <div className=" rounded-md p-4 bg-gray-50 border-border-gray border-2">
            <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium text-white uppercase bg-blue px-2 py-1 rounded-full font-body">
                        {question.type?.replace('_', ' ') || 'MULTIPLE CHOICE'}
                    </span>
                    <span className="text-xs font-medium text-blue uppercase border border-blue px-2 py-1 rounded-full font-body">
                        {question.score} POINTS
                    </span>
                </div>
                <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={onEdit} type="button" className="hover:text-blue">
                        <Edit2 className="w-4 h-4 text-gray-500 hover:text-blue" />
                    </Button>
                    {question.isExisting && question.questionId ? (
                        <DeleteQuestion
                            questionId={question.questionId}
                            questionText={question.question_text}
                            onDeleted={() => onRemove(index)}
                            triggerButtonProps={{
                                className: "text-gray-500 hover:text-blue",
                                size: "icon"
                            }}
                        />
                    ) : (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRemove(index)}
                            type="button"
                            className="text-gray-400 hover:text-red-500"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            <h3 className="font-medium mb-2 font-heading">{question.question_text}</h3>

            {/* Description type content */}
            {question.type === "description" && (
                <div className="bg-white rounded-md p-3 text-sm text-gray-700 mt-3">
                    {question.question_content && (
                        <div className="whitespace-pre-wrap mb-2">
                            <strong>Content:</strong> {question.question_content}
                        </div>
                    )}
                    {question.answer_text && (
                        <div className="whitespace-pre-wrap">
                            <strong>Expected Answer:</strong> {question.answer_text}
                        </div>
                    )}
                </div>
            )}

            {/* Multiple choice answers */}
            {['multiple_choice', 'numeric', 'true_false'].includes(question.type) && question.answers && (
                <div className="space-y-2 mt-3">
                    {question.answers.map((answer, answerIndex) => (
                        <div key={answerIndex} className="flex items-center gap-3">
                            <input
                                type="radio"
                                name={`correct_choice_${index}`}
                                checked={answer.is_correct}
                                onChange={() => onCorrectChoiceChange(index, answerIndex)}
                                className="h-4 w-4 text-red focus:ring-green-500 font-body"
                            />
                            <label className="text-sm text-gray-700 cursor-pointer font-body">
                                {answer.text || `Answer Option ${answerIndex + 1}`}
                            </label>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// Edit Question Dialog Component
function EditQuestionDialog({ open, setOpen, onQuestionUpdated, question }) {
    const questionSchema = z.object({
        type: z.enum(['multiple_choice', 'description', 'true_false', 'numeric']),
        question_text: z.string().min(1, "Question is required"),
        score: z.number().min(1, "Score must be at least 1"),
        order: z.number().min(1, "Order must be at least 1"),
        question_content: z.string().optional(),
        explanation: z.string().optional(),
        answers: z.array(z.object({
            text: z.string().min(1, "Answer is required"),
            is_correct: z.boolean()
        })).optional().default([{ text: "", is_correct: false }, { text: "", is_correct: false }]),
        answer_text: z.string().optional(),
    }).refine((data) => {
        if (data.type === 'multiple_choice') {
            const validAnswers = (data.answers || []).filter(a => a.text && a.text.trim() !== "")
            return validAnswers.length >= 2 && validAnswers.some(a => a.is_correct)
        }
        if (data.type === 'numeric') {
            const validAnswers = (data.answers || []).filter(answer => answer.text && answer.text.trim() !== "")
            return validAnswers.length >= 1 && validAnswers.some(a => a.is_correct)
        }
        if (data.type === 'true_false') {
            const validAnswers = (data.answers || []).filter(answer => answer.text && answer.text.trim() !== "")
            const texts = validAnswers.map(a => a.text.toLowerCase())
            return (
                validAnswers.length === 2 &&
                texts.includes("true") &&
                texts.includes("false") &&
                validAnswers.some(a => a.is_correct)
            )
        }
        return true
    }, {
        message: "Invalid answers for this question type",
        path: ["answers"]
    })

    const form = useForm({
        resolver: zodResolver(questionSchema),
        defaultValues: {
            type: "multiple_choice",
            question_text: "",
            score: 1,
            order: 1,
            question_content: "",
            explanation: "",
            answers: [
                { text: "", is_correct: false },
                { text: "", is_correct: false }
            ],
            answer_text: "",
        }
    })

    const [correctIndex, setCorrectIndex] = useState(null)

    const { fields: answers, append, remove, replace } = useFieldArray({
        control: form.control,
        name: "answers",
    })

    const questionType = form.watch("type")

    useEffect(() => {
        if (open) {
            if (form.getValues("type") === "multiple_choice") {
                const idx = form.getValues("answers").findIndex(a => a.is_correct);
                setCorrectIndex(idx >= 0 ? idx : null);
            } else {
                setCorrectIndex(null);
            }
        }
    }, [open]);

    // Update form when question changes
    useEffect(() => {
        if (open && question) {
            console.log("EditQuestionDialog: Dialog opened, resetting form with question:", question)
            form.reset({
                type: question.type || "multiple_choice",
                question_text: question.question_text || "",
                score: question.score || 1,
                order: question.order || 1,
                question_content: question.question_content || "",
                explanation: question.explanation || "",
                answers: question.answers && question.answers.length > 0
                    ? question.answers.map(a => ({
                        text: a.text,
                        is_correct: !!a.is_correct
                    }))
                    : [{ text: "", is_correct: false }, { text: "", is_correct: false }],
                answer_text: question.answer_text || "",
            })

            const idx = question.answers?.findIndex(a => a.is_correct) ?? null
            setCorrectIndex(idx >= 0 ? idx : null)
        }
    }, [open, question])

    const handleTypeChange = (newType) => {
        form.setValue("type", newType)
        if (newType === "description") {
            replace([])
            setCorrectIndex(null)
        } else if (newType === "multiple_choice" || newType === "numeric") {
            if (answers.length < 2) {
                replace([
                    { text: "", is_correct: false },
                    { text: "", is_correct: false }
                ])
            }
        } else if (newType === "true_false") {
            replace([
                { text: "True", is_correct: false },
                { text: "False", is_correct: false }
            ])
            setCorrectIndex(null)
        }
    }

    const onSubmit = (values, event) => {
        console.log("EditQuestionDialog: onSubmit called with:", values)
        console.log("EditQuestionDialog: Event:", event)

        // Prevent any default form submission behavior
        if (event) {
            event.preventDefault()
            event.stopPropagation()
        }

        try {
            const validAnswers = values.answers
                .map((a, idx) => ({
                    text: a.text.trim(),
                    is_correct: idx === correctIndex
                }))
                .filter(a => a.text)

            if (values.type === "multiple_choice") {
                if (validAnswers.length < 2) {
                    toast.error("Multiple choice questions need at least 2 answer options")
                    return
                }
                if (correctIndex === null) {
                    toast.error("Please select a correct answer.")
                    return
                }
            }

            const isNoChange =
                question &&
                values.type === question.type &&
                values.question_text === question.question_text &&
                values.score === question.score &&
                values.order === question.order &&
                values.question_content === question.question_content &&
                values.explanation === question.explanation &&
                JSON.stringify(validAnswers) === JSON.stringify(question.answers || []) &&
                (values.answer_text || "") === (question.answer_text || "")

            console.log("EditQuestionDialog: isNoChange check:", isNoChange)

            if (isNoChange) {
                toast.info("No changes detected")
                setOpen(false)
                return
            }

            const updatedQuestion = {
                ...question,
                type: values.type,
                question_text: values.question_text,
                score: values.score,
                order: values.order,
                question_content: values.question_content || "",
                explanation: values.explanation || "",
                answers: validAnswers,
                answer_text: values.answer_text || "",
            }

            console.log("EditQuestionDialog: Calling onQuestionUpdated with:", updatedQuestion)
            onQuestionUpdated(updatedQuestion)
            toast.success("Question updated successfully")
            setOpen(false)
        } catch (error) {
            console.error("Failed to update question:", error)
            toast.error("Failed to update question. Please try again.")
        }
    }

    const handleCancel = () => {
        setOpen(false)
        setCorrectIndex(null)
    }

    const addAnswer = () => {
        append({ text: "", is_correct: false });
    }

    const removeAnswer = (index) => {
        if (answers.length > 2) {
            remove(index);
            if (correctIndex === index) setCorrectIndex(null);
            else if (correctIndex > index) setCorrectIndex(correctIndex - 1);
        }
    }

    const handleSubmitClick = (e) => {
        console.log("EditQuestionDialog: Submit button clicked")
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit(onSubmit)(e)
    }

    const handleRadioChange = (index) => {
        setCorrectIndex(index);
        // Update is_correct in form state
        const currentAnswers = form.getValues("answers").map((a, idx) => ({
            ...a,
            is_correct: idx === index
        }));
        form.setValue("answers", currentAnswers);
    };

    if (!question) return null

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                className="max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0"
                aria-describedby="edit-question-description"
            >
                <DialogHeader className="px-6 py-4 border-b shrink-0">
                    <DialogTitle className="font-heading">Edit Question</DialogTitle>
                    <DialogDescription id="edit-question-description" className="font-body">
                        Update the question details below.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-2">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="edit-question-form">
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="font-heading">Question Type *</FormLabel>
                                            <FormControl>
                                                <Select value={field.value} onValueChange={handleTypeChange}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select type" className="font-body" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="multiple_choice" className="font-body">Multiple Choice</SelectItem>
                                                        <SelectItem value="true_false" className="font-body">True/False</SelectItem>
                                                        <SelectItem value="numeric" className="font-body">Numeric</SelectItem>
                                                        <SelectItem value="description" className="font-body">Description</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="score"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="font-heading">Points *</FormLabel>
                                            <FormControl>
                                                <Input {...field} type="number" min="1" placeholder="1" onChange={e => field.onChange(parseInt(e.target.value) || 1)} className="font-body" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="order"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="font-heading">Order</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                type="number"
                                                min="1"
                                                onChange={e => field.onChange(parseInt(e.target.value) || 1)}
                                                className="font-body"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="question_text"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="font-heading">Question *</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} placeholder="Enter your question here" rows={3} className="font-body" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="question_content"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="font-heading">Content (Optional)</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} placeholder="Enter additional content required for the question" className="font-body" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Answer Options Section */}
                            {questionType === 'multiple_choice' || questionType === 'numeric' ? (
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <FormLabel className="font-heading">Answer Options * (Choose the correct answer)</FormLabel>
                                        <Button
                                            type="button"
                                            onClick={addAnswer}
                                            variant="outline"
                                            className="text-red border-red hover:bg-blue hover:text-white hover:border-transparent font-body rounded-[8px]"
                                        >
                                            Add Option
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        {answers.map((answer, index) => (
                                            <div key={answer.id || index} className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name="correct_choice"
                                                    checked={correctIndex === index}
                                                    onChange={() => handleRadioChange(index)}
                                                    className="h-4 w-4 text-red focus:ring-red border-gray-300 font-body"
                                                />
                                                <FormField
                                                    control={form.control}
                                                    name={`answers.${index}.text`}
                                                    render={({ field }) => (
                                                        <FormItem className="flex-1 m-0">
                                                            <FormControl>
                                                                <Input
                                                                    {...field}
                                                                    type={questionType === "numeric" ? "number" : "text"}
                                                                    step={questionType === "numeric" ? "any" : undefined}
                                                                    placeholder={questionType === "numeric" ? "Enter numeric answer here" : "Enter option here"}
                                                                    onKeyDown={e => {
                                                                        if (e.key === "Enter") e.preventDefault();
                                                                    }}
                                                                    className="font-body"
                                                                />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={() => removeAnswer(index)}
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-gray-400 hover:text-red-500"
                                                    disabled={answers.length <= 2}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : questionType === 'true_false' ? (
                                <div>
                                    <FormLabel className="font-heading">Answer Options *(Choose the correct answer)</FormLabel>
                                    <div className="space-y-2">
                                        {["True", "False"].map((option, index) => (
                                            <div key={option} className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name="correct_choice"
                                                    checked={correctIndex === index}
                                                    onChange={() => handleRadioChange(index)}
                                                    className="h-4 w-4 text-red focus:ring-red border-gray-300 font-body"
                                                />
                                                <Input
                                                    value={option}
                                                    readOnly
                                                    className="font-body bg-gray-100"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <FormField
                                    control={form.control}
                                    name="answer_text"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Expected Answer (Optional)</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    {...field}
                                                    placeholder="Enter the expected answer or grading rubric for this description question..."
                                                    rows={4}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <FormField
                                control={form.control}
                                name="explanation"
                                render={({ field }) => (
                                    <FormItem className="mt-4">
                                        <FormLabel className="font-heading">Explanation (Optional)</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} placeholder="Provide an explanation for the correct answer" className="font-body" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </form>
                    </Form>
                </div>

                {/* Move DialogFooter outside the form */}
                <div className="flex justify-between px-6 py-4 border-t shrink-0 gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="w-[100px] border-red text-red hover:bg-blue hover:text-white hover:border-transparent rounded-[8px] font-heading"
                        disabled={false}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="edit-question-form"
                        className="bg-red text-white hover:bg-blue font-heading rounded-[8px]"
                        disabled={
                            (
                                ["multiple_choice", "numeric"].includes(questionType) &&
                                (correctIndex === null || answers.length < 2)
                            ) ||
                            (questionType === "true_false" && correctIndex === null)
                        }
                    >
                        Update Question
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}

// Fixed Add Question Dialog Component
function AddQuestionDialog({ open, setOpen, onQuestionAdded, nextOrder = 1 }) {
    const questionSchema = z.object({
        type: z.enum(['multiple_choice', 'description', 'true_false', 'numeric']),
        question_text: z.string().min(1, "Question is required"),
        score: z.number().min(1, "Score must be at least 1"),
        order: z.number().min(1, "Order must be at least 1"),
        question_content: z.string().optional(),
        explanation: z.string().optional(),
        answers: z.array(z.object({
            text: z.string().min(1, "Answer is required"),
            is_correct: z.boolean()
        })).default([{ text: "", is_correct: false }, { text: "", is_correct: false }]),
        answer_text: z.string().optional(), // Added for description questions
    }).refine((data) => {
        if (data.type === "multiple_choice") {
            const validAnswers = (data.answers || []).filter(answer => answer.text && answer.text.trim() !== "")
            return validAnswers.length >= 2 && validAnswers.some(a => a.is_correct)
        }
        if (data.type === 'numeric') {
            const validAnswers = (data.answers || []).filter(answer => answer.text && answer.text.trim() !== "")
            return validAnswers.length >= 1 && validAnswers.some(a => a.is_correct)
        }
        if (data.type === 'true_false') {
            const validAnswers = (data.answers || []).filter(answer => answer.text && answer.text.trim() !== "")
            const texts = validAnswers.map(a => a.text.toLowerCase())
            return (
                validAnswers.length === 2 &&
                texts.includes("true") &&
                texts.includes("false") &&
                validAnswers.some(a => a.is_correct)
            )
        }
        return true
    }, {
        message: "Invalid answers for this question type",
        path: ["answers"]
    })

    const form = useForm({
        resolver: zodResolver(questionSchema),
        defaultValues: {
            type: "multiple_choice",
            question_text: "",
            score: 1,
            order: nextOrder,
            question_content: "",
            explanation: "",
            answers: [
                { text: "", is_correct: false },
                { text: "", is_correct: false }
            ],
            answer_text: "", // Added for description questions
        }
    })

    const [correctIndex, setCorrectIndex] = useState(null)

    const { fields: answers, append, remove, replace } = useFieldArray({
        control: form.control,
        name: "answers",
    })

    useEffect(() => {
        if (open) {
            if (form.getValues("type") === "multiple_choice") {
                const idx = form.getValues("answers").findIndex(a => a.is_correct);
                setCorrectIndex(idx >= 0 ? idx : null);
            } else {
                setCorrectIndex(null);
            }
        }
    }, [open]);

    const questionType = form.watch("type")

    const handleTypeChange = (newType) => {
        form.setValue("type", newType)
        if (newType === "description") {
            replace([])
            setCorrectIndex(null)
        } else if (newType === "multiple_choice" || newType === "numeric") {
            if (answers.length < 2) {
                replace([
                    { text: "", is_correct: false },
                    { text: "", is_correct: false }
                ])
            }
        } else if (newType === "true_false") {
            replace([
                { text: "True", is_correct: false },
                { text: "False", is_correct: false }
            ])
            setCorrectIndex(null)
        }
    }

    const handleRadioChange = (index) => {
        setCorrectIndex(index);
        // Update is_correct in form state
        const currentAnswers = form.getValues("answers").map((a, idx) => ({
            ...a,
            is_correct: idx === index
        }));
        form.setValue("answers", currentAnswers);
    };

    const onSubmit = (values) => {
        try {
            const validAnswers = values.answers
                .map((a, idx) => ({
                    text: a.text.trim(),
                    is_correct: idx === correctIndex
                }))
                .filter(a => a.text)

            // For multiple choice questions, require at least 2 answers
            if (values.type === "multiple_choice") {
                if (validAnswers.length < 2) {
                    toast.error("Multiple choice questions need at least 2 answer options")
                    return
                }
                if (correctIndex === null) {
                    toast.error("Please select a correct answer.");
                    return;
                }
            }

            const newQuestion = {
                type: values.type,
                question_text: values.question_text,
                score: values.score,
                order: values.order,
                question_content: values.question_content || "",
                explanation: values.explanation || "",
                answers: validAnswers,
                answer_text: values.answer_text || "", // Include answer_text for description questions
            }

            onQuestionAdded(newQuestion)
            resetForm()
            setOpen(false)
            toast.success("Question added successfully!")

        } catch (error) {
            console.error("Failed to add question:", error)
            toast.error("Failed to add question. Please try again.")
        }
    }

    const resetForm = () => {
        form.reset({
            type: "multiple_choice",
            question_text: "",
            score: 1,
            order: nextOrder,
            question_content: "",
            explanation: "",
            answers: [
                { text: "", is_correct: false },
                { text: "", is_correct: false }
            ],
            answer_text: "",
        })
        replace([
            { text: "", is_correct: false },
            { text: "", is_correct: false }
        ])
        setCorrectIndex(null)
    }

    const handleCancel = () => {
        resetForm()
        setOpen(false)
        setCorrectIndex(null)
    }

    const addAnswer = () => {
        append({ text: "", is_correct: false });
    }

    const removeAnswer = (index) => {
        if (answers.length > 2) {
            remove(index)
            if (correctIndex === index) setCorrectIndex(null)
            else if (correctIndex > index) setCorrectIndex(correctIndex - 1)
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0" aria-describedby="add-question-description">
                <DialogHeader className="px-6 py-4 border-b shrink-0">
                    <DialogTitle className="font-heading">Add New Question</DialogTitle>
                    <DialogDescription id="add-question-description" className="font-body">
                        Create a new question for this quiz.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-2">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="add-question-form">
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="font-heading">Question Type *</FormLabel>
                                            <FormControl>
                                                <Select value={field.value} onValueChange={handleTypeChange}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select type" className="font-body" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="multiple_choice" className="font-body">Multiple Choice</SelectItem>
                                                        <SelectItem value="true_false" className="font-body">True/False</SelectItem>
                                                        <SelectItem value="numeric" className="font-body">Numeric</SelectItem>
                                                        <SelectItem value="description" className="font-body">Description</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="score"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="font-heading">Points *</FormLabel>
                                            <FormControl>
                                                <Input {...field} type="number" min="1" placeholder="1" onChange={e => field.onChange(parseInt(e.target.value) || 1)} className="font-body" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="order"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="font-heading">Order</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                type="number"
                                                min="1"
                                                onChange={e => field.onChange(parseInt(e.target.value) || 1)}
                                                className="font-body"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="question_text"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="font-heading">Question *</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} placeholder="Enter your question here" rows={3} className="font-body" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="question_content"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="font-heading">
                                            Content (Optional)

                                            <div className="flex items-center gap-1 text-xs text-gray-500 font-body">
                                                <Info className="w-3 h-3" />
                                                Markdown supported
                                            </div>
                                        </FormLabel>
                                        <FormControl>
                                            <Textarea {...field} placeholder="Enter additional content required for the question" className="font-body" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Answer Options Section */}
                            {questionType === 'multiple_choice' || questionType === 'numeric' ? (
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <FormLabel className="font-heading">Answer Options * (Choose the correct answer)</FormLabel>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="text-red border-red hover:bg-blue hover:text-white hover:border-transparent font-body rounded-[8px]"
                                            onClick={() => append({ text: "", is_correct: false })}
                                        >
                                            Add Option
                                        </Button>
                                    </div>
                                    <div className="space-y-2">
                                        {answers.map((answer, index) => (
                                            <div key={answer.id || index} className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name="correct_choice"
                                                    checked={correctIndex === index}
                                                    onChange={() => handleRadioChange(index)}
                                                    className="h-4 w-4 text-red focus:ring-red border-gray-300 font-body"
                                                />
                                                <FormField
                                                    control={form.control}
                                                    name={`answers.${index}.text`}
                                                    render={({ field }) => (
                                                        <FormItem className="flex-1 m-0">
                                                            <FormControl>
                                                                <Input
                                                                    {...field}
                                                                    type={questionType === "numeric" ? "number" : "text"}
                                                                    step={questionType === "numeric" ? "any" : undefined}
                                                                    placeholder={questionType === "numeric" ? "Enter numeric answer here" : "Enter option here"}
                                                                    onKeyDown={e => {
                                                                        if (e.key === "Enter") e.preventDefault();
                                                                    }}
                                                                    className="font-body"
                                                                />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                                <Button
                                                    type="button"
                                                    onClick={() => removeAnswer(index)}
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-gray-400 hover:text-red-500"
                                                    disabled={answers.length <= 2}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : questionType === 'true_false' ? (
                                <div>
                                    <FormLabel className="font-heading">Answer Options *(Choose the correct answer)</FormLabel>
                                    <div className="space-y-2">
                                        {["True", "False"].map((option, index) => (
                                            <div key={option} className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name="correct_choice"
                                                    checked={correctIndex === index}
                                                    onChange={() => handleRadioChange(index)}
                                                    className="h-4 w-4 text-red focus:ring-red border-gray-300 font-body"
                                                />
                                                <Input
                                                    value={option}
                                                    readOnly
                                                    className="font-body bg-gray-100"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <FormField
                                    control={form.control}
                                    name="answer_text"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="font-heading">Expected Answer (Optional)</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    {...field}
                                                    placeholder="Enter the expected answer or grading rubric for this description question..."
                                                    rows={4}
                                                    className="font-body"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <FormField
                                control={form.control}
                                name="explanation"
                                render={({ field }) => (
                                    <FormItem className="mt-4">
                                        <FormLabel className="font-heading">Explanation (Optional)</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} placeholder="Provide an explanation for the correct answer" className="font-body" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />


                        </form>
                    </Form>
                </div>
                <div className="flex justify-between px-6 py-4 border-t shrink-0 gap-2">
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={false}
                            className="w-[100px] border-red border-1 hover:bg-blue hover:text-white hover:border-transparent rounded-[8px] font-heading text-red"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        form="add-question-form"
                        className="bg-red text-white hover:bg-blue font-heading rounded-[8px]"
                        disabled={
                            (
                                ["multiple_choice", "numeric"].includes(questionType) &&
                                (correctIndex === null || answers.length < 2)
                            ) ||
                            (questionType === "true_false" && correctIndex === null)
                        }
                    >
                        Add Question
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}